import {
  Component,
  Input,
  ViewContainerRef,
  OnInit,
  OnDestroy,
  ComponentFactoryResolver,
  ComponentRef,
  Type,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { SubscriptionLike } from 'rxjs';

export type ComponentRendererComponentUpdater<TComponent, TData, TRendererData> = (
  componentInstance: TComponent,
  data: TData,
  rowData?: any,
  componentRef?: ComponentRef<TComponent>,
) => (() => void) | void;

@Component({
  standalone: false,
  selector: 'ti-renderer',
  template: `<ng-container #container></ng-container>`,
})
export class ComponentRendererComponent<TComponent, TData> implements OnInit, OnDestroy {
  @Input() component: Type<TComponent>;
  @Input() updater?: ComponentRendererComponentUpdater<TComponent, TData, any>;
  @Input() set data(data: TData) {
    this.setData(data);
  }
  @Input() rendererData: any;
  @ViewChild('container', { read: ViewContainerRef, static: true }) container: ViewContainerRef;

  private componentRef: ComponentRef<TComponent>;
  private initialized = false;
  private unsubscriber: (() => void) | SubscriptionLike | void;
  private _data: TData;

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {}

  public get renderedComponentRef(): ComponentRef<TComponent> {
    return this.componentRef;
  }

  ngOnDestroy() {
    this.destroyRenderedComponent();
  }

  ngOnInit() {
    this.renderComponent();
  }

  refresh(component?: Type<TComponent>, updater?: ComponentRendererComponentUpdater<TComponent, TData, any>) {
    this.destroyRenderedComponent();

    if (component !== undefined) {
      this.component = component;
      this.updater = updater;
    }

    this.renderComponent();
  }

  private renderComponent() {
    if (this.component) {
      const factory = this.componentFactoryResolver.resolveComponentFactory(this.component);
      this.componentRef = this.container.createComponent(factory);
      this.initialized = true;
      this.setData(this._data);
    }
  }

  private destroyRenderedComponent() {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
      this.unsubscribe();
    }
  }

  private setData(data: TData) {
    this._data = data;
    if (this.initialized && this.updater && this.componentRef) {
      this.unsubscribe();
      this.unsubscriber = this.updater(this.componentRef.instance, this._data, this.rendererData, this.componentRef);
      this.componentRef.injector.get(ChangeDetectorRef).markForCheck();
    }
  }

  private unsubscribe() {
    if (typeof this.unsubscriber === 'function') {
      this.unsubscriber();
    } else if (this.unsubscriber && this.unsubscriber.unsubscribe) {
      this.unsubscriber.unsubscribe();
    }
    this.unsubscriber = null;
  }
}
